import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  getTableOrderMenu, placeTableOrder, getActiveTableOrders, cancelTableOrder, updateTableOrderItemQty,
  updateTableOrderItemOption,
  loginQrOrderMember, getReviewableTableOrders,
  requestTableCall, getPendingTableCalls,
  createProductReview, createStoreReview,
  tableOrderStreamUrl,
  qrThumbnailImageUrl, qrMainImageUrl, qrOptionImageUrl,
  getSharedCart, addSharedCartItem, setSharedCartItemQty, removeSharedCartItem,
  getSideRecommends,
  getBrandInfo, brandLogoUrl, brandMainUrl,
} from '../api/qrOrder';
import { formatDateTime } from '../utils/format';
import ImageWithFallback from '../components/common/ImageWithFallback';
import TableOrderPayment from '../components/TableOrderPayment';
import '../styles/qrOrder.css';

// 고객이 QR코드로 접속하는 화면이다. 사이트 공용 헤더(Header.jsx)는 이 화면에서 숨긴다
// (자체 헤더(.to-header)를 이미 갖고 있어서 - App.jsx/Header.jsx 참고).
// 데이터(매장/테이블/메뉴/리뷰)는 front 백엔드가 갖고 있으므로 API 호출은 api/qrOrder.js 를 통해 나간다.
//
// 메뉴 탭은 네이티브 앱 스타일의 3단 플로우(목록→상세→옵션선택)로 동작한다. 목록에서 상품을
// 고르면 상세(이미지 캐러셀·설명)를 보고, "주문하기"로 옵션선택(단일선택+수량)에 진입해 담는다.
// 담긴 항목은 하단 고정 장바구니 바에서 주문한다. 주문내역·직원호출·회원로그인·리뷰는 그대로다.

const STATUS_LABEL = {
  RECEIVED: '접수됨', COOKING: '조리중', SERVED: '서빙완료', PAID: '결제완료', CANCELLED: '취소됨',
};

const MAX_DESC_LENGTH = 1000;

// 직원호출(콜벨) 사유 프리셋 - 레퍼런스 "직원 호출: 수저, 휴지 등 손님이 명확하게
// 요청해요" 벤치마킹. 기존엔 뭉뚱그린 "직원 호출" 버튼 하나뿐이라 무엇 때문에 불렀는지
// 관리자가 직접 memo 를 열어봐야 알 수 있었다 - 자주 쓰이는 사유를 구체 유형으로 분리한다.
const CALL_REASON_OPTIONS = [
  { type: 'STAFF', label: '직원 호출', icon: '🙋' },
  { type: 'WATER', label: '물', icon: '💧' },
  { type: 'SPOON', label: '수저', icon: '🥄' },
  { type: 'NAPKIN', label: '냅킨', icon: '🧻' },
  { type: 'SIDE_DISH', label: '반찬리필', icon: '🍚' },
  { type: 'PAYMENT', label: '계산요청', icon: '💳' },
  { type: 'ETC', label: '기타', icon: '✍️' },
];

// 다국어 메뉴판 - 레퍼런스 벤치마킹 기능. 한국어(KO)는 번역 테이블 없이 원문을 그대로 쓰고,
// 나머지 언어는 관리자가 등록한 TB_PRODUCT_TRANSLATION 번역으로 상품명/상세를 덮어써 내려받는다.
const LANGUAGE_OPTIONS = [
  { code: 'KO', label: '한국어' },
  { code: 'EN', label: 'English' },
  { code: 'JA', label: '日本語' },
  { code: 'ZH', label: '中文' },
];

/** 관리자가 리치텍스트 에디터로 입력한 HTML에서 태그를 제거해 순수 텍스트만 추출한다. */
function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}

/** 상품 설명은 화면이 지나치게 길어지지 않도록 최대 길이까지만 보여준다. */
function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

const won = (n) => `${Number(n || 0).toLocaleString()}원`;

/** 상품의 (사용중인) 옵션만 추린다. */
function sellableOptions(product) {
  return (product.options || []).filter((o) => o.useYn === 'Y');
}
function isOptionSoldOut(option) {
  return option.stockQty <= 0 || option.soldOutYn === 'Y';
}
/** 주문 가능한 옵션이 하나도 없으면 상품 전체 품절 */
function isProductSoldOut(product) {
  return sellableOptions(product).every(isOptionSoldOut);
}
/** 상품 대표가(할인 적용 시 할인가) */
function productPrice(product) {
  return product.discountActive ? product.discountedPrice : product.basePrice;
}
/** 옵션 1개 단가 = 대표가 + 옵션 추가금 */
function optionUnitPrice(product, option) {
  return productPrice(product) + option.additionalPrice;
}

// 결제(정산) 방식 (기획서 기준 2가지). 실제 PG 승인은 없고 주문에 방식/분담내역을 기록만 한다.
//   ITEM  : 상품별 결제 - 각자 주문한 상품만 골라 개별 결제
//   EQUAL : 1/N 분할 결제 - 전체 금액을 인원수로 균등 분할
const PAY_LABELS = { ITEM: '상품별 결제', EQUAL: '1/N 분할', NONE: '안 나눔' };
const PAY_METHODS = ['ITEM', 'EQUAL'];

// 수령방식 - 레퍼런스 "모든 주문 방식 지원(선불 픽업(카페))" 벤치마킹. 테이블 없이 카운터에서
// 포장 픽업하는 주문도 QR오더 화면에서 바로 선택할 수 있게 한다(실제 PG/선결제 변경 없이 표시만 구분).
//   DINE_IN  : 매장이용 - 기본값, 테이블로 서빙
//   TAKEOUT  : 포장 - 준비되면 카운터에서 픽업
const ORDER_TYPE_LABELS = { DINE_IN: '매장에서 먹을게요', TAKEOUT: '포장할게요' };
const ORDER_TYPES = ['DINE_IN', 'TAKEOUT'];

/**
 * 이 주문에 이미 승인된 카드 결제(NICEPAY)가 있는가.
 *
 * 있으면 고객 셀프 취소·수량변경 버튼을 감춘다 - 환불은 PG 취소 API 를 타야 해서 이 화면에서
 * 처리할 수 없기 때문이다(서버도 TableOrderService.rejectIfAlreadyPaid 로 동일하게 거부한다).
 * 판별에 paymentStatus 를 쓰면 안 된다: NICEPAY 연동이 꺼진 매장은 결제 없이도 주문 즉시
 * PAID 로 기록되므로 멀쩡한 주문의 버튼까지 사라진다. paid_amount 는 실제 승인 시에만 오른다.
 */
function hasApprovedPayment(order) {
  return Number(order?.paidAmount || 0) > 0;
}

/**
 * 결제 분담 계산. 실제 결제는 없고 분담 금액을 계산·표시하고 주문에 기록만 한다.
 *  - ITEM  : 각 장바구니 라인을 담당자(1~ways)에게 배정, 담당자별 합계
 *  - EQUAL : 총액을 인원수로 나눔(나머지는 앞사람부터 1원씩)
 * 반환: { shares:[{label, amount, optionIds}] }
 */
function computeSplit(cart, splitType, splitWays, itemAssign) {
  const total = cart.reduce((s, c) => s + c.unitPrice * c.qty, 0);
  const ways = Math.max(1, splitWays || 1);

  if (splitType === 'ITEM') {
    const shares = Array.from({ length: ways }, (_, i) => ({ label: `${i + 1}번`, amount: 0, optionIds: [] }));
    for (const c of cart) {
      const payer = Math.min(ways, Math.max(1, itemAssign[c.optionId] || 1)) - 1;
      shares[payer].amount += c.unitPrice * c.qty;
      shares[payer].optionIds.push(c.optionId);
    }
    return { shares };
  }

  // EQUAL (기본)
  const base = Math.floor(total / ways);
  const remainder = total - base * ways;
  const shares = Array.from({ length: ways }, (_, i) => ({
    label: `${i + 1}번`,
    amount: base + (i < remainder ? 1 : 0),
  }));
  return { shares };
}

export default function QrOrderPage() {
  const { qrToken } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 장바구니는 서버(Redis)가 테이블 단위로 보관하는 "공유 장바구니"다. 이 state 는 그 스냅샷이며,
  // 담기/수량변경/삭제는 API 호출 후 응답 스냅샷으로 갱신하고, 다른 기기의 변경은 SSE(cart-changed)로 반영된다.
  const [cart, setCart] = useState([]); // [{ optionId, productId, productName, optionName, unitPrice, qty }]
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ---- 결제 방식(정산 분담): 상품별(ITEM) / 1/N(EQUAL) ----
  const [splitType, setSplitType] = useState('EQUAL');
  const [splitWays, setSplitWays] = useState(2);
  const [itemAssign, setItemAssign] = useState({}); // optionId -> 담당자 번호(1~ways)

  // ---- 수령방식(매장이용/포장) - 레퍼런스 "선불 픽업(카페)" 벤치마킹 ----
  const [orderType, setOrderType] = useState('DINE_IN');

  // ---- 실시간 공유 장바구니 opt-in (기획서: "장바구니를 공유할까요?" 알림 → 수락 시 공유) ----
  // shared=false 면 이 기기 전용 로컬 장바구니, true 면 테이블 공유 장바구니(서버/Redis + SSE 동기화).
  const [shared, setShared] = useState(() => sessionStorage.getItem(`to_shared_${qrToken}`) === '1');
  const [sharePrompt, setSharePrompt] = useState(false); // 공유 요청 모달 표시

  // ---- 사이드 메뉴 제안(업셀) - 레퍼런스 "사이드 메뉴 제안으로 객단가가 상승해요" 벤치마킹 ----
  // 담기에 성공한 직후, 그 상품에 등록된 사이드 메뉴가 있으면 "이 메뉴와 잘 어울려요" 팝업으로 보여준다.
  const [sideRecommendPrompt, setSideRecommendPrompt] = useState(null); // { baseProductName, items: ProductVO[] } | null
  const [addedSideProductIds, setAddedSideProductIds] = useState(new Set()); // 팝업에서 이미 담은 사이드 상품ID
  const sharedRef = useRef(false);        // SSE 콜백에서 최신 shared 값을 읽기 위한 ref
  const shareDeclinedRef = useRef(false); // "따로 담기" 선택 시 재알림 방지
  const [tab, setTab] = useState('menu'); // 'menu' | 'orders' | 'brand'
  const [brandInfo, setBrandInfo] = useState(null); // 매장이 속한 브랜드 소개(없으면 null → 브랜드 탭 숨김)
  const [activeOrders, setActiveOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [updatingOptionItemId, setUpdatingOptionItemId] = useState(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);

  // 브랜드 소개 조회 - 매장이 브랜드에 속해 있고 사용중이면 브랜드 탭을 노출한다(로그인 무관, qrToken 만으로).
  useEffect(() => {
    let cancelled = false;
    getBrandInfo(qrToken)
      .then((res) => { if (!cancelled) setBrandInfo(res.data || null); })
      .catch(() => { if (!cancelled) setBrandInfo(null); });
    return () => { cancelled = true; };
  }, [qrToken]);
  // 메뉴 탭 분류 필터 - null 이면 "전체", 그 외에는 선택된 categoryId
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // ---- 다국어 메뉴판 - 테이블(qrToken)마다 마지막으로 고른 언어를 기억해둔다 ----
  const [langCode, setLangCode] = useState(() => sessionStorage.getItem(`to_lang_${qrToken}`) || 'KO');

  // ---- 메뉴 탭 내부 화면 전환 (네이티브 플로우): 목록 → 상세 → 옵션선택 ----
  const [menuView, setMenuView] = useState('list'); // 'list' | 'detail' | 'options'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [optQty, setOptQty] = useState(1);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // ---- 직원호출(콜벨) - 이미 처리 대기 중인 호출이 있으면 그 유형의 버튼을 비활성화해 중복
  // 호출을 막는다. 레퍼런스 "수저, 휴지 등 손님이 명확하게 요청해요" 벤치마킹으로
  // 뭉뚱그린 "직원 호출" 버튼 하나 대신 구체적인 사유를 고를 수 있는 패널을 연다 - 서로 다른
  // 사유는 동시에 각각 대기 중일 수 있어 callPending 을 유형별 Set 으로 관리한다.
  const [callPanelOpen, setCallPanelOpen] = useState(false);
  const [callPendingTypes, setCallPendingTypes] = useState(() => new Set());
  const [callRequesting, setCallRequesting] = useState(false);
  const [callResolvedFlash, setCallResolvedFlash] = useState(false);

  // ---- 회원 선택적 로그인 (QR오더는 기본적으로 비회원/GUEST 주문, 로그인은 선택) ----
  const [memberToken, setMemberToken] = useState(() => sessionStorage.getItem(`to_member_token_${qrToken}`) || '');
  const [memberName, setMemberName] = useState(() => sessionStorage.getItem(`to_member_name_${qrToken}`) || '');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginForm, setLoginForm] = useState({ memberAccount: '', memberPassword: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ---- 리뷰 작성 (결제완료(PAID)된 본인 주문에 한해, 로그인한 경우에만 노출) ----
  const [reviewableOrders, setReviewableOrders] = useState([]);
  const [reviewDrafts, setReviewDrafts] = useState({}); // key -> { rating, content }
  const [reviewSubmitting, setReviewSubmitting] = useState(new Set());
  const [reviewedKeys, setReviewedKeys] = useState(new Set());

  // 하단 고정 장바구니 바의 실제 높이를 측정해 목록 하단 여백에 반영한다(가림 방지).
  const cartBarRef = useRef(null);
  const [cartBarHeight, setCartBarHeight] = useState(0);

  useEffect(() => {
    const el = cartBarRef.current;
    if (!el) {
      setCartBarHeight(0);
      return undefined;
    }
    const update = () => setCartBarHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [cart.length, memo, menuView]);

  // silent=true 는 SSE 로 실시간 메뉴 갱신을 받았을 때 쓴다 - 전체화면 로딩 없이 조용히 반영.
  const fetchMenu = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const res = await getTableOrderMenu(qrToken, langCode);
      setContext(res.data);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.message || '메뉴를 불러오지 못했습니다. QR코드를 다시 확인해주세요.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [qrToken, langCode]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const handleChangeLanguage = (code) => {
    setLangCode(code);
    sessionStorage.setItem(`to_lang_${qrToken}`, code);
  };

  const fetchActiveOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await getActiveTableOrders(qrToken);
      setActiveOrders(res.data || []);
    } catch {
      setActiveOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [qrToken]);

  useEffect(() => { fetchActiveOrders(); }, [fetchActiveOrders]);

  // 조리 시작 전(RECEIVED)에만 고객이 직접 취소할 수 있다 - 최종 검증은 서버가 한다(TableOrderService.cancelOwnOrder).
  const handleCancelOrder = async (tableOrderId) => {
    if (!window.confirm('이 주문을 취소하시겠어요?')) {
      return;
    }
    setCancelingOrderId(tableOrderId);
    try {
      await cancelTableOrder(qrToken, tableOrderId);
      fetchActiveOrders();
    } catch (err) {
      window.alert(err.response?.data?.message || '주문 취소에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setCancelingOrderId(null);
    }
  };

  // 조리 시작 전(RECEIVED)에만 고객이 직접 수량을 바꿀 수 있다(최종 검증은 서버).
  // newQty가 0이면 그 항목을 아예 빼는 것이라 재확인을 받는다(마지막 항목이면 주문 전체가 취소됨).
  const handleUpdateItemQty = async (tableOrderId, tableOrderItemId, newQty) => {
    if (newQty <= 0 && !window.confirm('이 메뉴를 주문에서 빼시겠어요?')) {
      return;
    }
    setUpdatingItemId(tableOrderItemId);
    try {
      await updateTableOrderItemQty(qrToken, tableOrderId, tableOrderItemId, Math.max(newQty, 0));
      fetchActiveOrders();
    } catch (err) {
      window.alert(err.response?.data?.message || '주문 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUpdatingItemId(null);
    }
  };

  // 조리 시작 전(RECEIVED)에만 고객이 직접 옵션(예: ICE→HOT)을 바꿀 수 있다(최종 검증은 서버).
  // 담긴 상품/수량은 그대로 두고 옵션과 금액만 바뀐다 - 취소 후 재담기보다 훨씬 빠르다.
  const handleUpdateItemOption = async (tableOrderId, tableOrderItemId, newOptionId) => {
    setUpdatingOptionItemId(tableOrderItemId);
    try {
      await updateTableOrderItemOption(qrToken, tableOrderId, tableOrderItemId, Number(newOptionId));
      fetchActiveOrders();
    } catch (err) {
      window.alert(err.response?.data?.message || '옵션 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUpdatingOptionItemId(null);
    }
  };

  const fetchPendingCalls = useCallback(async () => {
    try {
      const res = await getPendingTableCalls(qrToken);
      const pending = res.data || [];
      setCallPendingTypes(new Set(pending.map((c) => c.callType)));
    } catch {
      // 조회 실패는 버튼 상태에 영향을 주지 않는다 (기존 상태 유지).
    }
  }, [qrToken]);

  useEffect(() => { fetchPendingCalls(); }, [fetchPendingCalls]);

  const handleCallStaff = async (callType) => {
    if (callPendingTypes.has(callType) || callRequesting) return;
    let memo;
    if (callType === 'ETC') {
      memo = window.prompt('어떤 도움이 필요하신가요? (예: 재떨이 교체)') || '';
      if (!memo.trim()) return; // 취소하거나 빈 내용이면 호출을 보내지 않는다.
    }
    setCallRequesting(true);
    try {
      await requestTableCall(qrToken, callType, memo);
      setCallPendingTypes((prev) => new Set(prev).add(callType));
      setCallPanelOpen(false);
      const label = CALL_REASON_OPTIONS.find((o) => o.type === callType)?.label || '직원';
      window.alert(`${label} 호출을 접수했습니다. 곧 도와드리겠습니다.`);
    } catch (err) {
      window.alert(err.response?.data?.message || '직원호출에 실패했습니다. 다시 시도해주세요.');
      fetchPendingCalls();
    } finally {
      setCallRequesting(false);
    }
  };

  const fetchReviewableOrders = useCallback(async () => {
    if (!memberToken) {
      setReviewableOrders([]);
      return;
    }
    try {
      const res = await getReviewableTableOrders(qrToken, memberToken);
      setReviewableOrders(res.data || []);
    } catch {
      setReviewableOrders([]);
    }
  }, [qrToken, memberToken]);

  useEffect(() => { fetchReviewableOrders(); }, [fetchReviewableOrders]);

  // 서버에서 들어온 공유 장바구니 스냅샷을 화면에 반영한다.
  //  - 이미 공유 중이면 최신 스냅샷으로 갱신.
  //  - 아직 공유 전이고 누군가 담기 시작했으면 "공유할까요?" 알림을 띄운다(기획서 flow).
  // (SSE cart-changed 수신과 최초 조회(fetchCart)가 동일 로직이라 여기로 통합한다.)
  const applyIncomingCart = useCallback((items) => {
    if (sharedRef.current) {
      setCart(items);
    } else if (items.length > 0 && !shareDeclinedRef.current) {
      setSharePrompt(true);
    }
  }, []);

  // 주문상태 실시간 반영: 서버가 SSE 로 상태 변경을 push 하면 즉시 주문내역을 다시 불러온다.
  useEffect(() => {
    if (!qrToken) return undefined;

    const eventSource = new EventSource(tableOrderStreamUrl(qrToken));

    eventSource.addEventListener('connected', () => setLiveConnected(true));
    eventSource.addEventListener('status-changed', () => {
      fetchActiveOrders();
      fetchReviewableOrders();
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 2000);
    });
    eventSource.addEventListener('menu-changed', () => {
      fetchMenu({ silent: true });
    });
    // 같은 테이블의 다른 기기가 공유 장바구니를 바꾸면 서버가 최신 스냅샷을 push 한다.
    eventSource.addEventListener('cart-changed', (e) => {
      try {
        applyIncomingCart(JSON.parse(e.data) || []);
      } catch {
        // 파싱 실패 시 무시 (다음 이벤트/재조회로 복구).
      }
    });
    eventSource.addEventListener('call-changed', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.callStatus === 'DONE') {
          setCallResolvedFlash(true);
          setTimeout(() => setCallResolvedFlash(false), 3000);
        }
      } catch {
        // 파싱 실패해도 아래 재조회는 계속 진행한다.
      }
      fetchPendingCalls();
    });
    eventSource.onerror = () => {
      setLiveConnected(false);
    };

    return () => eventSource.close();
  }, [qrToken, fetchActiveOrders, fetchMenu, fetchReviewableOrders, fetchPendingCalls, applyIncomingCart]);

  // shared 최신값을 SSE 콜백에서 읽기 위한 ref 동기화
  useEffect(() => { sharedRef.current = shared; }, [shared]);

  // ---- 장바구니 ----
  // shared=false: 이 기기 전용 로컬 장바구니(로컬 state 조작).
  // shared=true : 테이블 공유 장바구니(서버 API 조작 + SSE 동기화).
  // 진입 시 공유 장바구니에 이미 담긴 게 있으면(다른 일행이 담는 중) "공유할까요?" 알림을 띄운다.
  const fetchCart = useCallback(async () => {
    try {
      const res = await getSharedCart(qrToken);
      applyIncomingCart(res.data || []);
    } catch {
      // 장바구니 조회 실패는 화면을 막지 않는다.
    }
  }, [qrToken, applyIncomingCart]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (product, option, addQty = 1) => {
    if (!shared) {
      setCart((prev) => {
        const existing = prev.find((c) => c.optionId === option.optionId);
        if (existing) {
          return prev.map((c) => (c.optionId === option.optionId ? { ...c, qty: c.qty + addQty } : c));
        }
        return [...prev, {
          optionId: option.optionId,
          productId: product.productId,
          productName: product.productName,
          optionName: option.optionName,
          unitPrice: optionUnitPrice(product, option),
          qty: addQty,
        }];
      });
      return;
    }
    try {
      const res = await addSharedCartItem(qrToken, product.productId, option.optionId, addQty);
      setCart(res.data || []);
    } catch (err) {
      window.alert(err.response?.data?.message || '담기에 실패했습니다.');
    }
  };

  const changeQty = async (optionId, delta) => {
    const line = cart.find((c) => c.optionId === optionId);
    if (!line) return;
    if (!shared) {
      setCart((prev) => prev
        .map((c) => (c.optionId === optionId ? { ...c, qty: c.qty + delta } : c))
        .filter((c) => c.qty > 0));
      return;
    }
    try {
      const res = await setSharedCartItemQty(qrToken, optionId, line.qty + delta);
      setCart(res.data || []);
    } catch (err) {
      window.alert(err.response?.data?.message || '수량 변경에 실패했습니다.');
    }
  };

  const removeCartLine = async (optionId) => {
    if (!shared) {
      setCart((prev) => prev.filter((c) => c.optionId !== optionId));
      return;
    }
    try {
      const res = await removeSharedCartItem(qrToken, optionId);
      setCart(res.data || []);
    } catch (err) {
      window.alert(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  // 장바구니 공유 시작/수락 - 현재 로컬 장바구니 항목을 공유 장바구니로 올리고 공유 모드로 전환한다.
  const acceptShare = async () => {
    setSharePrompt(false);
    try {
      let snapshot = null;
      for (const c of cart) {
        const res = await addSharedCartItem(qrToken, c.productId, c.optionId, c.qty);
        snapshot = res.data;
      }
      if (!snapshot) {
        const res = await getSharedCart(qrToken);
        snapshot = res.data;
      }
      setShared(true);
      sessionStorage.setItem(`to_shared_${qrToken}`, '1');
      setCart(snapshot || []);
    } catch (err) {
      window.alert(err.response?.data?.message || '장바구니 공유에 실패했습니다.');
    }
  };

  const declineShare = () => {
    setSharePrompt(false);
    shareDeclinedRef.current = true;
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.unitPrice * c.qty, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  // ---- 메뉴 플로우 전환 ----
  const openDetail = (product) => {
    setSelectedProduct(product);
    setCarouselIndex(0);
    setMenuView('detail');
    window.scrollTo(0, 0);
  };
  const openOptions = () => {
    const opts = sellableOptions(selectedProduct).filter((o) => !isOptionSoldOut(o));
    setSelectedOptionId(opts.length === 1 ? opts[0].optionId : null);
    setOptQty(1);
    setMenuView('options');
    window.scrollTo(0, 0);
  };

  // 추천 메뉴 칩 탭: 페이지 이동 없이 아래 메뉴 그리드의 해당 상품 카드로 스크롤 이동 + 잠깐 강조.
  //
  const scrollToMenuCard = (productId) => {
    const el = document.getElementById(`menu-card-${productId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('to-flow-card-highlight');
    window.setTimeout(() => el.classList.remove('to-flow-card-highlight'), 1200);
  };

  // 담기 직후 이 상품에 등록된 사이드 메뉴 제안이 있으면 업셀 팝업을 띄운다.
  //
  // 이어 미구현으로 남아있던 나머지 절반. 팝업은 담기 흐름을 막지 않도록 담기 성공 후에만 띄운다.)
  const maybeShowSideRecommend = async (product) => {
    try {
      const res = await getSideRecommends(product.productId);
      const items = res.data || [];
      if (items.length === 0) return;
      setAddedSideProductIds(new Set());
      setSideRecommendPrompt({ baseProductName: product.productName, items });
    } catch {
      // 사이드 메뉴 제안 조회 실패는 담기 자체를 막지 않는다(부가 기능).
    }
  };

  const closeSideRecommendPrompt = () => setSideRecommendPrompt(null);

  /** 업셀 팝업에서 사이드 상품 담기 - 옵션이 여러 개여도 팝업 흐름을 유지하기 위해 첫 판매가능 옵션으로 담는다. */
  const addSideRecommendItem = async (sideProduct) => {
    const opts = sellableOptions(sideProduct).filter((o) => !isOptionSoldOut(o));
    if (opts.length === 0) return;
    await addToCart(sideProduct, opts[0], 1);
    setAddedSideProductIds((prev) => new Set(prev).add(sideProduct.productId));
  };

  // 목록 카드의 "담기" 빠른 담기: 옵션이 하나뿐이면 바로 장바구니에 담고,
  // 여러 개면 옵션을 골라야 하므로 옵션선택 화면으로 보낸다.
  const quickAdd = async (product) => {
    const opts = sellableOptions(product).filter((o) => !isOptionSoldOut(o));
    if (opts.length === 0) return;
    if (opts.length === 1) {
      await addToCart(product, opts[0], 1);
      await maybeShowSideRecommend(product);
      return;
    }
    setSelectedProduct(product);
    setSelectedOptionId(null);
    setOptQty(1);
    setMenuView('options');
    window.scrollTo(0, 0);
  };
  const menuBack = () => {
    setMenuView((v) => (v === 'options' ? 'detail' : 'list'));
    window.scrollTo(0, 0);
  };
  const addSelectedToCart = async () => {
    const option = sellableOptions(selectedProduct).find((o) => o.optionId === selectedOptionId);
    if (!option) return;
    await addToCart(selectedProduct, option, optQty);
    setMenuView('list');
    window.scrollTo(0, 0);
    await maybeShowSideRecommend(selectedProduct);
  };
  const switchTab = (next) => {
    setTab(next);
    setMenuView('list'); // 탭 전환 시 메뉴는 항상 목록부터
  };

  const changeSplitWays = (n) => setSplitWays(Math.max(1, n));

  const handleSubmitOrder = async () => {
    if (cart.length === 0) return;

    // 주문전용(ORDER_ONLY) 매장은 결제 방식 선택 자체가 없으므로 분담 내역을 계산/전송하지 않는다
    // (서버도 매장 정산방식을 다시 확인해 강제로 NONE 처리하지만, 클라이언트도 불필요한 계산을 하지 않는다).
    const orderOnly = settlementMode === 'ORDER_ONLY';
    const split = orderOnly ? null : computeSplit(cart, splitType, splitWays, itemAssign);
    const splitPayload = {
      ...(orderOnly ? {} : {
        splitType,
        splitWays,
        splitDetail: JSON.stringify({ type: splitType, ways: splitWays, shares: split.shares }),
      }),
      // 수령방식도 같은 payload 에 실어 보낸다 (placeTableOrder 가 이 객체를 그대로 요청 본문에 펼친다).
      orderType,
    };

    setSubmitting(true);
    try {
      await placeTableOrder(
        qrToken, memo,
        cart.map((c) => ({ productId: c.productId, optionId: c.optionId, qty: c.qty })),
        memberToken,
        splitPayload,
      );
      let message = orderType === 'TAKEOUT' ? '포장 주문이 접수되었습니다! 준비되면 카운터에서 알려드려요.' : '주문이 접수되었습니다!';
      if (settlementMode === 'POSTPAID') {
        message += ' 결제는 나중에 카운터에서 진행돼요.';
      } else if (settlementMode === 'ORDER_ONLY') {
        message += ' 결제는 카운터에서 진행돼요.';
      }
      window.alert(message);
      setCart([]); // 공유 시 서버도 비우고 SSE 로 반영되지만, 즉시성을 위해 로컬도 비운다.
      setMemo('');
      setSplitType('EQUAL');
      setOrderType('DINE_IN');
      setTab('orders');
      fetchActiveOrders();
    } catch (err) {
      window.alert(err.response?.data?.message || '주문에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- 회원 선택적 로그인 ----
  const handleLogin = async () => {
    if (!loginForm.memberAccount || !loginForm.memberPassword) {
      setLoginError('아이디와 비밀번호를 입력해주세요.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      // 계정은 트림해 보낸다(가입 저장값과 일치). 비밀번호는 그대로 둔다.
      const res = await loginQrOrderMember(loginForm.memberAccount.trim(), loginForm.memberPassword);
      const { accessToken, memberName: name } = res.data;
      setMemberToken(accessToken);
      setMemberName(name);
      sessionStorage.setItem(`to_member_token_${qrToken}`, accessToken);
      sessionStorage.setItem(`to_member_name_${qrToken}`, name);
      setShowLoginModal(false);
      setLoginForm({ memberAccount: '', memberPassword: '' });
    } catch (err) {
      setLoginError(err.response?.data?.message || '로그인에 실패했습니다.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setMemberToken('');
    setMemberName('');
    sessionStorage.removeItem(`to_member_token_${qrToken}`);
    sessionStorage.removeItem(`to_member_name_${qrToken}`);
  };

  // ---- 리뷰 작성 ----
  const updateReviewDraft = (key, patch) => {
    setReviewDrafts((prev) => ({ ...prev, [key]: { rating: 5, content: '', ...prev[key], ...patch } }));
  };

  // 상품/매장 리뷰 제출 공통 처리 - 제출 중 표시(Set)·완료 표시·에러 안내가 동일하고,
  // key 접두어·호출할 API·성공 문구만 다르므로 하나로 묶는다.
  const submitReview = async (key, apiCall, successMsg) => {
    setReviewSubmitting((prev) => new Set(prev).add(key));
    try {
      await apiCall();
      setReviewedKeys((prev) => new Set(prev).add(key));
      window.alert(successMsg);
    } catch (err) {
      window.alert(err.response?.data?.message || '리뷰 등록에 실패했습니다.');
    } finally {
      setReviewSubmitting((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const submitProductReview = (tableOrderItemId) => {
    const draft = reviewDrafts[`item-${tableOrderItemId}`] || { rating: 5, content: '' };
    return submitReview(
      `item-${tableOrderItemId}`,
      () => createProductReview(memberToken, tableOrderItemId, draft.rating, draft.content),
      '상품 리뷰가 등록되었습니다. 감사합니다!',
    );
  };

  const submitStoreReview = (tableOrderId) => {
    const draft = reviewDrafts[`store-${tableOrderId}`] || { rating: 5, content: '' };
    return submitReview(
      `store-${tableOrderId}`,
      () => createStoreReview(memberToken, tableOrderId, draft.rating, draft.content),
      '매장 리뷰가 등록되었습니다. 감사합니다!',
    );
  };

  if (loading) {
    return <div className="to-screen"><div className="to-loading">불러오는 중...</div></div>;
  }
  if (error) {
    return <div className="to-screen"><div className="to-loading to-error">{error}</div></div>;
  }

  const { table, menu } = context;
  // 매장의 주문 정산 방식 (PREPAID=선결제(기본)/POSTPAID=후결제/ORDER_ONLY=주문전용) -
  // 레퍼런스 "모든 주문 방식 지원" 벤치마킹. 체크아웃 화면의 결제 방식 패널 노출 여부를 결정한다.
  const settlementMode = context.settlementMode || 'PREPAID';

  // 메뉴에 실제로 존재하는 분류만 뽑아 "전체 + 분류별" 필터 칩을 만든다.
  const categories = [];
  const seenCategoryIds = new Set();
  for (const p of menu) {
    if (p.categoryId != null && !seenCategoryIds.has(p.categoryId)) {
      seenCategoryIds.add(p.categoryId);
      categories.push({ categoryId: p.categoryId, categoryName: p.categoryName || '기타' });
    }
  }
  const activeCategoryId = selectedCategoryId != null && seenCategoryIds.has(selectedCategoryId)
    ? selectedCategoryId
    : null;
  const filteredMenu = activeCategoryId == null
    ? menu
    : menu.filter((p) => p.categoryId === activeCategoryId);

  // 메뉴 상세/옵션 서브뷰에서는 상단 탭/멤버바를 감추고 뒤로가기 바를 보여준다.
  const inMenuFlow = tab === 'menu' && menuView !== 'list';

  return (
    <div className="to-screen">
      <div className="to-header">
        <img src="/onetak-icon.svg" alt="onetak" className="to-brand-icon" />
        <div className="to-store-name">{table.storeName}</div>
        <div className="to-table-no">테이블 {table.tableNo}</div>
        <select
          className="to-lang-select"
          value={langCode}
          onChange={(e) => handleChangeLanguage(e.target.value)}
          aria-label="메뉴 언어 선택"
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>{opt.label}</option>
          ))}
        </select>
      </div>

      {!inMenuFlow && (
        <div className="to-member-bar">
          <div className="to-call-staff-wrap">
            <button
              type="button"
              className="to-call-staff-btn"
              onClick={() => setCallPanelOpen((open) => !open)}
              disabled={callRequesting}
            >
              {callPendingTypes.size > 0 ? `호출 접수됨 (${callPendingTypes.size})` : '직원 호출'}
            </button>
            {callResolvedFlash && <span className="to-call-resolved-badge">직원이 확인했어요</span>}
            {callPanelOpen && (
              <>
                {/* 레퍼런스 "수저, 휴지 등 손님이 명확하게 요청해요" 벤치마킹 - 뭉뚱그린
                    호출 대신 사유를 골라 보낸다. 이미 접수된 사유는 다시 누르지 못하게 막는다. */}
                <div className="to-call-reason-backdrop" onClick={() => setCallPanelOpen(false)} />
                <div className="to-call-reason-panel">
                  <div className="to-call-reason-title">무엇을 도와드릴까요?</div>
                  <div className="to-call-reason-grid">
                    {CALL_REASON_OPTIONS.map((opt) => {
                      const pending = callPendingTypes.has(opt.type);
                      return (
                        <button
                          key={opt.type}
                          type="button"
                          className="to-call-reason-btn"
                          onClick={() => handleCallStaff(opt.type)}
                          disabled={pending || callRequesting}
                        >
                          <span className="to-call-reason-icon">{opt.icon}</span>
                          <span>{pending ? '접수됨' : opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          {memberToken ? (
            <>
              <span className="to-member-name">{memberName}님</span>
              <button type="button" className="to-member-action" onClick={handleLogout}>로그아웃</button>
            </>
          ) : (
            <button type="button" className="to-member-action" onClick={() => setShowLoginModal(true)}>
              회원 로그인 (선택)
            </button>
          )}
        </div>
      )}

      {showLoginModal && (
        <div className="to-modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="to-modal" onClick={(e) => e.stopPropagation()}>
            <div className="to-modal-title">회원 로그인</div>
            <div className="to-modal-desc">로그인하면 주문 내역이 회원 계정에 연결되고, 결제 완료 후 리뷰를 작성할 수 있어요. 로그인하지 않아도 주문은 그대로 가능합니다.</div>
            <input
              className="to-modal-input"
              placeholder="아이디"
              value={loginForm.memberAccount}
              onChange={(e) => setLoginForm((f) => ({ ...f, memberAccount: e.target.value }))}
            />
            <input
              className="to-modal-input"
              type="password"
              placeholder="비밀번호"
              value={loginForm.memberPassword}
              onChange={(e) => setLoginForm((f) => ({ ...f, memberPassword: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            />
            {loginError && <div className="to-modal-error">{loginError}</div>}
            <div className="to-modal-actions">
              <button type="button" className="to-modal-cancel" onClick={() => setShowLoginModal(false)}>취소</button>
              <button type="button" className="to-modal-submit" onClick={handleLogin} disabled={loginLoading}>
                {loginLoading ? '로그인 중...' : '로그인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사이드 메뉴 제안(업셀) 팝업 - 담기 직후 노출 */}
      {sideRecommendPrompt && (
        <div className="to-modal-overlay" onClick={closeSideRecommendPrompt}>
          <div className="to-modal" onClick={(e) => e.stopPropagation()}>
            <div className="to-modal-title">🍟 이 메뉴와 잘 어울려요</div>
            <div className="to-modal-desc">{sideRecommendPrompt.baseProductName}와(과) 함께 곁들이면 좋아요.</div>
            <div className="to-side-recommend-modal-list">
              {sideRecommendPrompt.items.map((side) => {
                const added = addedSideProductIds.has(side.productId);
                return (
                  <div className="to-side-recommend-modal-item" key={side.productId}>
                    <ImageWithFallback
                      src={qrThumbnailImageUrl(side.productId)}
                      alt={side.productName}
                      className="to-side-recommend-modal-thumb"
                    />
                    <div className="to-side-recommend-modal-info">
                      <div className="to-side-recommend-modal-name">{side.productName}</div>
                      <div className="to-side-recommend-modal-price">+{won(productPrice(side))}</div>
                    </div>
                    <button
                      type="button"
                      className={`to-side-recommend-modal-add${added ? ' added' : ''}`}
                      disabled={added}
                      onClick={() => addSideRecommendItem(side)}
                    >
                      {added ? '담김' : '담기'}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="to-modal-actions">
              <button type="button" className="to-modal-cancel" onClick={closeSideRecommendPrompt}>괜찮아요</button>
              <button type="button" className="to-modal-submit" onClick={closeSideRecommendPrompt}>다 담았어요</button>
            </div>
          </div>
        </div>
      )}

      {/* 장바구니 공유 요청 알림 (기획서: 같은 QR 고객에게 "장바구니를 공유할까요?") */}
      {sharePrompt && (
        <div className="to-modal-overlay" onClick={declineShare}>
          <div className="to-modal" onClick={(e) => e.stopPropagation()}>
            <div className="to-modal-title">장바구니를 공유할까요?</div>
            <div className="to-modal-desc">같은 테이블의 일행이 함께 담고 있어요. 공유하면 한 장바구니에 같이 담아 한 번에 주문할 수 있습니다.</div>
            <div className="to-modal-actions">
              <button type="button" className="to-modal-cancel" onClick={declineShare}>따로 담을게요</button>
              <button type="button" className="to-modal-submit" onClick={acceptShare}>공유하기</button>
            </div>
          </div>
        </div>
      )}

      {!inMenuFlow && (
        <div className="to-tabs">
          <button className={tab === 'menu' ? 'active' : ''} onClick={() => switchTab('menu')}>메뉴</button>
          <button className={tab === 'orders' ? 'active' : ''} onClick={() => switchTab('orders')}>
            주문내역
            {liveConnected && <span className="to-live-dot" title="실시간 연결됨" />}
            {justUpdated && <span className="to-updated-badge">업데이트</span>}
          </button>
          {brandInfo && (
            <button className={tab === 'brand' ? 'active' : ''} onClick={() => switchTab('brand')}>브랜드</button>
          )}
        </div>
      )}

      {/* ===== 브랜드 소개 탭 ===== */}
      {tab === 'brand' && brandInfo && (
        <div className="to-brand">
          {brandInfo.mainImagePath && (
            <ImageWithFallback src={brandMainUrl(brandInfo.brandId)} alt={brandInfo.brandName} className="to-brand-main" />
          )}
          <div className="to-brand-head">
            {brandInfo.logoImagePath && (
              <ImageWithFallback src={brandLogoUrl(brandInfo.brandId)} alt={brandInfo.brandName} className="to-brand-logo" />
            )}
            <div className="to-brand-name">{brandInfo.brandName}</div>
          </div>
          {brandInfo.introContent && (
            <div className="to-brand-intro" dangerouslySetInnerHTML={{ __html: brandInfo.introContent }} />
          )}
          {(brandInfo.homepageUrl || brandInfo.snsUrl) && (
            <div className="to-brand-links">
              {brandInfo.homepageUrl && <a href={brandInfo.homepageUrl} target="_blank" rel="noreferrer">홈페이지</a>}
              {brandInfo.snsUrl && <a href={brandInfo.snsUrl} target="_blank" rel="noreferrer">SNS</a>}
            </div>
          )}
        </div>
      )}

      {inMenuFlow && (
        <div className="to-flow-back-bar">
          <button type="button" className="to-flow-back" onClick={menuBack} aria-label="뒤로">← 뒤로</button>
        </div>
      )}

      {/* ===== 메뉴 탭 - 목록 ===== */}
      {tab === 'menu' && menuView === 'list' && (
        <>
          {/* 추천 메뉴 - 관리자가 상품관리에서 지정한 추천메뉴를 "전체" 분류일 때만 가로 스크롤로 보여준다.
              탭하면 아래 그리드의 실제 상품 카드로 스크롤 이동한다(별도 상세 화면으로 이동하지 않음). */}
          {activeCategoryId == null && menu.some((p) => p.recommendYn === 'Y' && !isProductSoldOut(p)) && (
            <div className="to-recommend-strip">
              <div className="to-recommend-title">🔥 추천 메뉴</div>
              <div className="to-recommend-scroll">
                {menu.filter((p) => p.recommendYn === 'Y' && !isProductSoldOut(p)).map((product) => (
                  <button
                    type="button"
                    key={product.productId}
                    className="to-recommend-chip"
                    onClick={() => scrollToMenuCard(product.productId)}
                  >
                    <ImageWithFallback
                      src={qrThumbnailImageUrl(product.productId)}
                      alt={product.productName}
                      className="to-recommend-chip-thumb"
                    />
                    <span className="to-recommend-chip-name">{product.productName}</span>
                    <span className="to-recommend-chip-price">{won(productPrice(product))}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div className="to-category-filter">
              <button
                type="button"
                className={`to-category-chip${activeCategoryId == null ? ' active' : ''}`}
                onClick={() => setSelectedCategoryId(null)}
              >
                전체
              </button>
              {categories.map((c) => (
                <button
                  type="button"
                  key={c.categoryId}
                  className={`to-category-chip${activeCategoryId === c.categoryId ? ' active' : ''}`}
                  onClick={() => setSelectedCategoryId(c.categoryId)}
                >
                  {c.categoryName}
                </button>
              ))}
            </div>
          )}

          {/* 장바구니 공유 배너 - 공유 전이면 공유 시작 버튼, 공유 중이면 상태 표시 */}
          {shared ? (
            <div className="to-share-banner shared">👥 일행과 장바구니를 공유하고 있어요</div>
          ) : (
            <button type="button" className="to-share-banner" onClick={acceptShare}>
              👥 일행과 장바구니 공유하기
            </button>
          )}

          <div
            className="to-flow-grid"
            style={cart.length > 0 ? { paddingBottom: cartBarHeight + 16 } : undefined}
          >
            {menu.length === 0 && <div className="to-empty">현재 주문 가능한 메뉴가 없습니다.</div>}
            {menu.length > 0 && filteredMenu.length === 0 && (
              <div className="to-empty">이 분류에 주문 가능한 메뉴가 없습니다.</div>
            )}
            {filteredMenu.map((product) => (
              <MenuGridCard key={product.productId} product={product} onOpen={openDetail} onQuickAdd={quickAdd} />
            ))}
          </div>
        </>
      )}

      {/* ===== 메뉴 탭 - 상세 ===== */}
      {tab === 'menu' && menuView === 'detail' && selectedProduct && (
        <DetailView
          product={selectedProduct}
          carouselIndex={carouselIndex}
          setCarouselIndex={setCarouselIndex}
          onOrder={openOptions}
        />
      )}

      {/* ===== 메뉴 탭 - 옵션선택 ===== */}
      {tab === 'menu' && menuView === 'options' && selectedProduct && (
        <OptionsView
          product={selectedProduct}
          selectedOptionId={selectedOptionId}
          onSelectOption={setSelectedOptionId}
          qty={optQty}
          setQty={setOptQty}
          onAdd={addSelectedToCart}
        />
      )}

      {/* ===== 주문내역 탭 ===== */}
      {tab === 'orders' && (
        <div className="to-order-list">
          {loadingOrders && <div className="to-empty">불러오는 중...</div>}
          {!loadingOrders && activeOrders.length === 0 && <div className="to-empty">진행 중인 주문이 없습니다.</div>}
          {!loadingOrders && activeOrders.map((o) => (
            <div className="to-order-card" key={o.tableOrderId}>
              <div className="to-order-card-header">
                <span>
                  <span className={`to-status-badge to-status-${o.orderStatus}`}>{STATUS_LABEL[o.orderStatus] || o.orderStatus}</span>
                  {o.orderType === 'TAKEOUT' && <span className="to-status-badge to-ordertype-badge">포장</span>}
                  {o.paymentStatus === 'PENDING' && <span className="to-status-badge to-paymentstatus-badge">결제대기</span>}
                  {o.paymentStatus === 'PARTIALLY_PAID' && <span className="to-status-badge to-paymentstatus-badge">부분결제</span>}
                </span>
                <span className="to-order-time">{formatDateTime(o.createdAt)}</span>
              </div>
              {(o.items || []).map((item) => (
                <div className="to-order-item" key={item.tableOrderItemId}>
                  <span className="to-order-item-name">
                    {item.productName} ({item.optionName})
                    {o.orderStatus === 'RECEIVED' && !hasApprovedPayment(o) ? (
                      <span className="to-order-item-qty-edit">
                        <button
                          type="button"
                          className="to-order-item-qty-btn"
                          disabled={updatingItemId === item.tableOrderItemId}
                          onClick={() => handleUpdateItemQty(o.tableOrderId, item.tableOrderItemId, item.qty - 1)}
                        >
                          −
                        </button>
                        <span className="to-order-item-qty-value">x{item.qty}</span>
                        <button
                          type="button"
                          className="to-order-item-qty-btn"
                          disabled={updatingItemId === item.tableOrderItemId}
                          onClick={() => handleUpdateItemQty(o.tableOrderId, item.tableOrderItemId, item.qty + 1)}
                        >
                          +
                        </button>
                      </span>
                    ) : ` x${item.qty}`}
                    {o.orderStatus === 'RECEIVED' && (() => {
                      // 같은 상품(productId)의 다른 옵션(예: ICE→HOT)으로 즉시 바꿀 수 있는 드롭다운.
                      // 현재 메뉴에서 그 상품을 찾을 수 없거나(품목 삭제 등) 고를 수 있는 다른 옵션이
                      // 없으면(선택지가 1개뿐이면) 굳이 보여주지 않는다.
                      const product = menu.find((p) => p.productId === item.productId);
                      if (!product) return null;
                      const choices = sellableOptions(product).filter(
                        (opt) => opt.optionId === item.optionId || !isOptionSoldOut(opt)
                      );
                      if (choices.length < 2) return null;
                      return (
                        <select
                          className="to-order-item-option-select"
                          value={item.optionId}
                          disabled={updatingOptionItemId === item.tableOrderItemId}
                          onChange={(e) => handleUpdateItemOption(o.tableOrderId, item.tableOrderItemId, e.target.value)}
                        >
                          {choices.map((opt) => (
                            <option key={opt.optionId} value={opt.optionId}>{opt.optionName}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </span>
                  <span>{Number(item.amount).toLocaleString()}원</span>
                </div>
              ))}
              {o.memo && (
                <div className="to-order-memo">
                  <span className="to-order-memo-label">요청사항</span>
                  <span className="to-order-memo-text">{o.memo}</span>
                </div>
              )}
              <div className="to-order-total">합계 {Number(o.totalAmount).toLocaleString()}원</div>
              {o.splitType && o.splitType !== 'NONE' && (
                <div className="to-order-split">
                  결제 방식: {PAY_LABELS[o.splitType] || o.splitType}
                  {o.splitWays ? ` · ${o.splitWays}명` : ''}
                </div>
              )}
              {(o.paymentStatus === 'PENDING' || o.paymentStatus === 'PARTIALLY_PAID' || o.paymentStatus === 'PAID') && (
                <TableOrderPayment qrToken={qrToken} order={o} onPaid={fetchActiveOrders} />
              )}
              {/*
                이미 승인된 결제가 있는 주문은 환불이 필요하므로 셀프 취소 버튼을 감춘다.
                고객은 결제 영역(TableOrderPayment) 안내나 직원호출로 처리한다.
              */}
              {o.orderStatus === 'RECEIVED' && !hasApprovedPayment(o) && (
                <button
                  type="button"
                  className="to-order-cancel-btn"
                  disabled={cancelingOrderId === o.tableOrderId}
                  onClick={() => handleCancelOrder(o.tableOrderId)}
                >
                  {cancelingOrderId === o.tableOrderId ? '취소 처리 중...' : '주문 취소'}
                </button>
              )}
            </div>
          ))}

          {memberToken && (
            <div className="to-review-section">
              <div className="to-review-section-title">리뷰 작성 가능한 주문</div>
              {reviewableOrders.length === 0 && (
                <div className="to-empty">결제완료된 주문이 없습니다. 결제가 완료되면 여기에서 리뷰를 작성할 수 있어요.</div>
              )}
              {reviewableOrders.map((o) => (
                <ReviewableOrderCard
                  key={o.tableOrderId}
                  order={o}
                  drafts={reviewDrafts}
                  onDraftChange={updateReviewDraft}
                  onSubmitItem={submitProductReview}
                  onSubmitStore={submitStoreReview}
                  submittingKeys={reviewSubmitting}
                  reviewedKeys={reviewedKeys}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== 메뉴 탭 - 장바구니(수량 변경/삭제) ===== */}
      {tab === 'menu' && menuView === 'cart' && (
        <CartView
          cart={cart}
          total={cartTotal}
          memo={memo}
          setMemo={setMemo}
          onChangeQty={changeQty}
          onRemove={removeCartLine}
          onSubmit={handleSubmitOrder}
          submitting={submitting}
          onBackToMenu={() => { setMenuView('list'); window.scrollTo(0, 0); }}
          splitType={splitType}
          setSplitType={setSplitType}
          splitWays={splitWays}
          setSplitWays={changeSplitWays}
          itemAssign={itemAssign}
          setItemAssign={setItemAssign}
          orderType={orderType}
          setOrderType={setOrderType}
          settlementMode={settlementMode}
        />
      )}

      {/* 하단 고정 장바구니 바 - 메뉴 목록에서 담긴 항목이 있을 때만 노출.
          "주문하기"를 누르면 장바구니(체크아웃) 화면으로 이동해 결제 방식을 고르고 최종 주문한다. */}
      {tab === 'menu' && menuView === 'list' && cart.length > 0 && (
        <div className="to-cart-bar" ref={cartBarRef}>
          {/* 담은 메뉴+옵션 미리보기 (수량 변경/삭제·결제 방식은 다음 화면에서) */}
          <div className="to-cart-lines">
            {cart.map((c) => (
              <div className="to-cart-line" key={c.optionId}>
                <span className="to-cart-line-name">
                  {c.productName} · {c.optionName} × {c.qty}
                </span>
                <span className="to-cart-line-price">{won(c.unitPrice * c.qty)}</span>
                <button
                  type="button"
                  className="to-cart-line-remove"
                  onClick={() => removeCartLine(c.optionId)}
                  aria-label={`${c.productName} 삭제`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="to-cart-summary">
            <span>{cartCount}개</span>
            <span className="to-cart-total">{cartTotal.toLocaleString()}원</span>
          </div>
          <button
            className="to-submit-btn"
            onClick={() => { setMenuView('cart'); window.scrollTo(0, 0); }}
          >
            주문하기
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 메뉴 목록의 상품 1개 카드 (2단 그리드).
 * 카드(이미지·이름·가격)를 탭하면 상세로 이동하고, 하단 "담기" 버튼은 빠른 담기다
 * (옵션 1개면 바로 담고, 여러 개면 옵션선택 화면으로 이동). 카드 탭 영역과 담기 버튼은
 * 형제 버튼이라 중첩(button in button)되지 않는다.
 */
function MenuGridCard({ product, onOpen, onQuickAdd }) {
  const soldOut = isProductSoldOut(product);
  return (
    <div className="to-flow-card" id={`menu-card-${product.productId}`}>
      <button
        type="button"
        className="to-flow-card-tap"
        onClick={() => !soldOut && onOpen(product)}
        disabled={soldOut}
      >
        <div className="to-flow-thumb-wrap">
          <ImageWithFallback
            src={qrThumbnailImageUrl(product.productId)}
            alt={product.productName}
            className="to-flow-thumb"
          />
          {soldOut && <span className="to-flow-soldout">품절</span>}
          {!soldOut && product.discountActive && <span className="to-flow-badge">할인</span>}
          {!soldOut && product.bestSeller && <span className="to-flow-badge-best">🔥 인기</span>}
        </div>
        <div className="to-flow-card-body">
          <div className="to-flow-card-name">{product.productName}</div>
          <div className="to-flow-card-price">
            {product.discountActive ? (
              <>
                <span className="to-flow-price-old">{won(product.basePrice)}</span>
                <span className="to-flow-price-now">{won(product.discountedPrice)}~</span>
              </>
            ) : (
              <span className="to-flow-price-now">{won(product.basePrice)}~</span>
            )}
          </div>
        </div>
      </button>
      <button
        type="button"
        className="to-flow-add-btn"
        onClick={() => onQuickAdd(product)}
        disabled={soldOut}
      >
        {soldOut ? '품절' : '+ 담기'}
      </button>
    </div>
  );
}

/** 상품 상세: 이미지 캐러셀 + 설명 + 가격 + 하단 고정 "주문하기"(→ 옵션선택) */
function DetailView({ product, carouselIndex, setCarouselIndex, onOrder }) {
  const images = [];
  if (product.mainImagePath) images.push(qrMainImageUrl(product.productId));
  if (product.thumbnailImagePath) images.push(qrThumbnailImageUrl(product.productId));
  const desc = truncateText(stripHtml(product.detailContent || product.precautions || ''), MAX_DESC_LENGTH);
  const soldOut = isProductSoldOut(product);
  const idx = Math.min(carouselIndex, Math.max(0, images.length - 1));

  return (
    <div className="to-flow-detail">
      <div className="to-flow-carousel">
        {images.length > 0 ? (
          <ImageWithFallback src={images[idx]} alt={product.productName} className="to-flow-carousel-img" />
        ) : (
          <div className="to-flow-carousel-img" />
        )}
        {images.length > 1 && (
          <div className="to-flow-dots">
            {images.map((src, i) => (
              <button
                type="button"
                key={src}
                className={`to-flow-dot${i === idx ? ' active' : ''}`}
                onClick={() => setCarouselIndex(i)}
                aria-label={`이미지 ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="to-flow-detail-body">
        <div className="to-flow-detail-name">{product.productName}</div>
        {desc && <div className="to-flow-detail-desc">{desc}</div>}
        <div className="to-flow-detail-price">
          {product.discountActive ? (
            <>
              <span className="to-flow-price-old">{won(product.basePrice)}</span>
              <span className="to-flow-price-now">{won(product.discountedPrice)}</span>
            </>
          ) : (
            <span>{won(product.basePrice)}</span>
          )}
        </div>
      </div>

      <div className="to-flow-bottom">
        <button type="button" className="to-flow-cta" onClick={onOrder} disabled={soldOut}>
          {soldOut ? '품절' : '주문하기'}
        </button>
      </div>
    </div>
  );
}

/** 옵션선택: 상품 요약 + 옵션 단일선택 + 수량 + 하단 고정 합계/담기 */
function OptionsView({ product, selectedOptionId, onSelectOption, qty, setQty, onAdd }) {
  const options = sellableOptions(product);
  const selected = options.find((o) => o.optionId === selectedOptionId);
  const lineTotal = selected ? optionUnitPrice(product, selected) * qty : 0;

  return (
    <div className="to-flow-options">
      <div className="to-flow-opt-summary">
        <ImageWithFallback
          src={qrThumbnailImageUrl(product.productId)}
          alt={product.productName}
          className="to-flow-opt-summary-thumb"
        />
        <div className="to-flow-opt-summary-name">{product.productName}</div>
      </div>

      <div className="to-flow-opt-title">옵션을 선택해 주세요.</div>
      <div className="to-flow-opt-grid">
        {options.map((o) => {
          const soldOut = isOptionSoldOut(o);
          const active = o.optionId === selectedOptionId;
          return (
            <button
              type="button"
              key={o.optionId}
              className={`to-flow-opt-card${active ? ' active' : ''}${soldOut ? ' soldout' : ''}`}
              onClick={() => !soldOut && onSelectOption(o.optionId)}
              disabled={soldOut}
            >
              {o.optionImagePath && (
                <ImageWithFallback
                  src={qrOptionImageUrl(o.optionId)}
                  alt={o.optionName}
                  className="to-flow-opt-card-img"
                />
              )}
              <span className="to-flow-opt-card-name">{o.optionName}</span>
              {o.additionalPrice > 0 && <span className="to-flow-opt-card-add">+{won(o.additionalPrice)}</span>}
              {soldOut && <span className="to-flow-opt-card-soldout">품절</span>}
            </button>
          );
        })}
      </div>

      <div className="to-flow-bottom to-flow-bottom-options">
        <div className="to-flow-qty">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="수량 감소">−</button>
          <span>{qty}</span>
          <button type="button" onClick={() => setQty((q) => q + 1)} aria-label="수량 증가">+</button>
        </div>
        <button type="button" className="to-flow-cta" onClick={onAdd} disabled={!selected}>
          {selected ? `${won(lineTotal)} 담기` : '옵션 선택'}
        </button>
      </div>
    </div>
  );
}

/** 장바구니 편집: 라인별 수량 변경/삭제 + 결제 방식(상품별/1N) + 요청사항 + 하단 고정 주문하기 */
function CartView({
  cart, total, memo, setMemo, onChangeQty, onRemove, onSubmit, submitting, onBackToMenu,
  splitType, setSplitType, splitWays, setSplitWays, itemAssign, setItemAssign,
  orderType, setOrderType, settlementMode,
}) {
  if (cart.length === 0) {
    return (
      <div className="to-flow-cart-empty">
        <div className="to-empty">장바구니가 비어 있습니다.</div>
        <button type="button" className="to-flow-cta to-flow-cta-ghost" onClick={onBackToMenu}>메뉴 보러가기</button>
      </div>
    );
  }

  const split = computeSplit(cart, splitType, splitWays, itemAssign);

  return (
    <div className="to-flow-cart">
      <div className="to-flow-cart-list">
        {cart.map((c) => (
          <div className="to-flow-cart-line" key={c.optionId}>
            <ImageWithFallback
              src={qrThumbnailImageUrl(c.productId)}
              alt={c.productName}
              className="to-flow-cart-thumb"
            />
            <div className="to-flow-cart-info">
              <div className="to-flow-cart-name">{c.productName}</div>
              <div className="to-flow-cart-option">{c.optionName}</div>
              <div className="to-flow-cart-price">{won(c.unitPrice * c.qty)}</div>
            </div>
            <div className="to-flow-cart-actions">
              <button type="button" className="to-flow-cart-remove" onClick={() => onRemove(c.optionId)} aria-label="삭제">✕</button>
              <div className="to-flow-qty to-flow-qty-sm">
                <button type="button" onClick={() => onChangeQty(c.optionId, -1)} aria-label="수량 감소">−</button>
                <span>{c.qty}</span>
                <button type="button" onClick={() => onChangeQty(c.optionId, 1)} aria-label="수량 증가">+</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <OrderTypePanel orderType={orderType} setOrderType={setOrderType} />

      {/*
        주문 정산 방식에 따라 결제 방식 패널을 분기한다.
          PREPAID(선결제, 기본)  : 지금까지와 동일하게 결제 방식(상품별/1N) 패널을 그대로 보여준다.
          POSTPAID(후결제)      : 패널은 그대로 보여주되, 실제 결제는 나중에 진행된다는 안내를 덧붙인다.
          ORDER_ONLY(주문전용)  : 패널 자체를 숨기고, 결제는 카운터/POS에서 처리된다는 안내만 보여준다.
      */}
      {settlementMode === 'ORDER_ONLY' ? (
        <div className="to-split to-ordertype">
          <div className="to-split-title">결제 방식</div>
          <div className="to-split-note">이 매장은 결제를 카운터에서 진행해요. 아래 내용으로 주문만 접수돼요.</div>
        </div>
      ) : (
        <SplitPanel
          cart={cart}
          total={total}
          split={split}
          splitType={splitType}
          setSplitType={setSplitType}
          splitWays={splitWays}
          setSplitWays={setSplitWays}
          itemAssign={itemAssign}
          setItemAssign={setItemAssign}
          postpaidNote={settlementMode === 'POSTPAID'}
        />
      )}

      <div className="to-flow-cart-memo-wrap">
        <input
          className="to-memo-input"
          placeholder="요청사항 (선택)"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
      </div>

      <div className="to-flow-bottom to-flow-bottom-cart">
        <div className="to-flow-cart-total">
          <span>합계</span>
          <span className="to-flow-cart-total-amt">{won(total)}</span>
        </div>
        <button type="button" className="to-flow-cta" onClick={onSubmit} disabled={submitting}>
          {submitting ? '주문 중...' : '주문하기'}
        </button>
      </div>
    </div>
  );
}

/**
 * 수령방식 패널 - 레퍼런스 "모든 주문 방식 지원(선불 픽업(카페))" 벤치마킹.
 * 매장이용(DINE_IN, 기본)/포장(TAKEOUT) 중 고른다. 실제 결제 시점은 바뀌지 않고(여전히 매장/POS
 * 정산) 주방·카운터가 테이블 서빙 여부를 구분할 수 있도록 표시만 남긴다.
 */
function OrderTypePanel({ orderType, setOrderType }) {
  return (
    <div className="to-split to-ordertype">
      <div className="to-split-title">수령 방식</div>
      <div className="to-split-tabs">
        {ORDER_TYPES.map((t) => (
          <button
            type="button"
            key={t}
            className={`to-split-tab${orderType === t ? ' active' : ''}`}
            onClick={() => setOrderType(t)}
          >
            {ORDER_TYPE_LABELS[t]}
          </button>
        ))}
      </div>
      {orderType === 'TAKEOUT' && (
        <div className="to-split-note">포장 주문은 테이블로 가져다 드리지 않아요. 준비되면 카운터에서 받아가세요.</div>
      )}
    </div>
  );
}

/**
 * 결제 방식 패널 (기획서 2가지): 상품별 결제(ITEM) / 1/N 분할(EQUAL).
 * 두 방식 모두 인원 수를 정하고, 상품별은 각 항목의 담당자를 지정한다. 분담 요약을 함께 보여준다.
 * (실제 PG 승인은 없고 선택한 방식·분담내역이 주문에 기록된다.)
 */
function SplitPanel({ cart, total, split, splitType, setSplitType, splitWays, setSplitWays, itemAssign, setItemAssign, postpaidNote }) {
  const assignItem = (optionId, payer) => {
    setItemAssign((prev) => ({ ...prev, [optionId]: payer }));
  };

  return (
    <div className="to-split">
      <div className="to-split-title">결제 방식</div>
      {postpaidNote && (
        <div className="to-split-note">이 매장은 후결제 매장이에요. 지금은 주문만 접수되고, 결제는 나중에 카운터에서 진행돼요.</div>
      )}
      <div className="to-split-tabs">
        {PAY_METHODS.map((t) => (
          <button
            type="button"
            key={t}
            className={`to-split-tab${splitType === t ? ' active' : ''}`}
            onClick={() => setSplitType(t)}
          >
            {PAY_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="to-split-body">
        <div className="to-split-ways">
          <span>인원</span>
          <div className="to-flow-qty to-flow-qty-sm">
            <button type="button" onClick={() => setSplitWays(splitWays - 1)} aria-label="인원 감소">−</button>
            <span>{splitWays}</span>
            <button type="button" onClick={() => setSplitWays(splitWays + 1)} aria-label="인원 증가">+</button>
          </div>
        </div>

        {splitType === 'ITEM' && (
          <div className="to-split-items">
            {cart.map((c) => (
              <div className="to-split-item-row" key={c.optionId}>
                <span className="to-split-item-name">{c.productName} ({c.optionName}) x{c.qty}</span>
                <select
                  className="to-split-select"
                  value={Math.min(splitWays, itemAssign[c.optionId] || 1)}
                  onChange={(e) => assignItem(c.optionId, Number(e.target.value))}
                >
                  {Array.from({ length: splitWays }, (_, i) => (
                    <option key={i} value={i + 1}>{i + 1}번</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="to-split-summary">
          {split.shares.map((s, i) => (
            <div className="to-split-summary-row" key={i}>
              <span>{s.label}</span>
              <span>{won(s.amount)}</span>
            </div>
          ))}
          <div className="to-split-summary-total">
            <span>총액</span>
            <span>{won(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 1~5점 별점 선택 버튼 행 */
function StarRating({ value, onChange, disabled }) {
  return (
    <div className="to-star-row">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`to-star${n <= value ? ' filled' : ''}`}
          onClick={() => onChange(n)}
          disabled={disabled}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/**
 * 결제완료(PAID)된 주문 1건 카드 - 주문에 포함된 각 상품에 대한 상품 리뷰와,
 * 이 방문(주문) 전체에 대한 매장 리뷰를 함께 작성할 수 있다.
 */
function ReviewableOrderCard({ order, drafts, onDraftChange, onSubmitItem, onSubmitStore, submittingKeys, reviewedKeys }) {
  const storeKey = `store-${order.tableOrderId}`;
  const storeDraft = drafts[storeKey] || { rating: 5, content: '' };
  const storeDone = reviewedKeys.has(storeKey);
  const storeSubmitting = submittingKeys.has(storeKey);

  return (
    <div className="to-review-order-card">
      <div className="to-order-card-header">
        <span className="to-order-time">{formatDateTime(order.createdAt)}</span>
        <span className="to-order-total">합계 {Number(order.totalAmount).toLocaleString()}원</span>
      </div>

      {(order.items || []).map((item) => {
        const itemKey = `item-${item.tableOrderItemId}`;
        const itemDraft = drafts[itemKey] || { rating: 5, content: '' };
        const itemDone = reviewedKeys.has(itemKey);
        const itemSubmitting = submittingKeys.has(itemKey);
        return (
          <div className="to-review-item" key={item.tableOrderItemId}>
            <div className="to-review-item-name">{item.productName} ({item.optionName})</div>
            {itemDone ? (
              <div className="to-review-done">리뷰 작성 완료 - 감사합니다!</div>
            ) : (
              <>
                <StarRating
                  value={itemDraft.rating}
                  onChange={(n) => onDraftChange(itemKey, { rating: n })}
                  disabled={itemSubmitting}
                />
                <textarea
                  className="to-review-textarea"
                  placeholder="상품에 대한 리뷰를 남겨주세요 (선택)"
                  value={itemDraft.content}
                  onChange={(e) => onDraftChange(itemKey, { content: e.target.value })}
                  disabled={itemSubmitting}
                />
                <button
                  type="button"
                  className="to-review-submit-btn"
                  onClick={() => onSubmitItem(item.tableOrderItemId)}
                  disabled={itemSubmitting}
                >
                  {itemSubmitting ? '등록 중...' : '상품 리뷰 등록'}
                </button>
              </>
            )}
          </div>
        );
      })}

      <div className="to-review-item to-review-store">
        <div className="to-review-item-name">이번 방문(매장) 리뷰</div>
        {storeDone ? (
          <div className="to-review-done">리뷰 작성 완료 - 감사합니다!</div>
        ) : (
          <>
            <StarRating
              value={storeDraft.rating}
              onChange={(n) => onDraftChange(storeKey, { rating: n })}
              disabled={storeSubmitting}
            />
            <textarea
              className="to-review-textarea"
              placeholder="매장 이용 경험을 남겨주세요 (선택)"
              value={storeDraft.content}
              onChange={(e) => onDraftChange(storeKey, { content: e.target.value })}
              disabled={storeSubmitting}
            />
            <button
              type="button"
              className="to-review-submit-btn"
              onClick={() => onSubmitStore(order.tableOrderId)}
              disabled={storeSubmitting}
            >
              {storeSubmitting ? '등록 중...' : '매장 리뷰 등록'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
